import React, { useCallback, useEffect, useState } from 'react';
import { IconAsChunk } from '@boschebike/ebds-react-icons';
import classNames from '../helpers/classNames.js';
import { areFilesValid } from './private/upload-form-utils/handleFilesAction.js';
const UploadForm = ({
  id,
  value,
  valueOnError,
  valueOnSuccess,
  name,
  accept = 'image/png, image/jpeg',
  icon,
  onChange,
  onError,
  withMultiple,
  withNoFailOnInvalid,
  withFilesLimit,
  fileTypeValidator,
  fileSizeValidator,
  fileResolutionValidator,
  customStyle
}) => {
  const [text, setText] = useState(value);
  const [isOver, setIsOver] = useState(false);
  const [isError, setIsError] = useState(false);
  const [uploadFiles, setUploadFiles] = useState(null);
  const [inputChanged, setInputChanged] = useState(0);
  const [inputNode, setInputNode] = useState();
  const handleTextChange = useCallback(() => {
    if (isOver && valueOnSuccess) {
      return setText(valueOnSuccess);
    }
    if (isError && valueOnError) {
      return setText(valueOnError);
    }
    return setText(value);
  }, [isOver, isError, value, valueOnSuccess, valueOnError]);
  const handleDrop = useCallback(event => {
    event.preventDefault();
    const {
      dataTransfer
    } = event;
    let files;
    if (dataTransfer.files?.length) {
      files = withMultiple ? dataTransfer.files : dataTransfer.files[0];
    }
    if (files) {
      setUploadFiles(files);
    }
  }, [setUploadFiles, withMultiple]);
  const handleDragOver = useCallback(event => {
    event.stopPropagation();
    event.preventDefault();
  }, []);
  const handleChange = useCallback(event => {
    let files;
    if (event.target.files?.length > 0) {
      files = withMultiple ? event.target.files : event.target.files[0];
    }
    if (files) {
      setUploadFiles(files);
    }

    // When user chooses (by click) same files one after another.
    // The input doesn't send onChange event so nothing happens.
    // So on every change, we make sure the useEffect below really kicks in.
    // By rasing this dummy flag.
    setInputChanged(inputChanged + 1);
  }, [inputChanged, setUploadFiles, withMultiple]);
  const wrapperClassName = classNames('ebds-upload-form-container__label', {
    '--over': isOver,
    '--error': isError
  });
  useEffect(() => {
    // Check if there is a stored object provided by user
    // And proceed with validation
    if (uploadFiles !== null) {
      areFilesValid(uploadFiles, withNoFailOnInvalid, withFilesLimit, accept, fileTypeValidator, fileSizeValidator, fileResolutionValidator).then(result => {
        onChange?.(result);
        setIsOver(true);
        setIsError(false);
      }).catch(error => {
        console.log(error.message);
        onError?.(error);
        setIsOver(false);
        setIsError(true);
        setUploadFiles(null);
      });
      return;
    }

    // If there's no DOM node, abort. Nothing to reset.
    if (!inputNode) {
      return;
    }

    // Now, file is an url string, reset input.
    inputNode.value = '';
  }, [uploadFiles, inputNode, inputChanged, accept, withNoFailOnInvalid, withFilesLimit, fileTypeValidator, fileSizeValidator, fileResolutionValidator, onChange, onError]);
  useEffect(() => {
    handleTextChange();
  }, [isOver, isError, handleTextChange]);
  const style = {
    ...customStyle,
    ...{}
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "ebds-upload-form-container"
  }, /*#__PURE__*/React.createElement("label", {
    className: wrapperClassName,
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    htmlFor: id,
    style: isError ? style?.isError : isOver && style?.isOver || style?.default
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true
  }, icon || /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "UploadFrame"
  })), /*#__PURE__*/React.createElement("span", null, text)), /*#__PURE__*/React.createElement("input", {
    id: id,
    className: "ebds-sr-only",
    type: "file",
    accept: accept,
    multiple: withMultiple,
    name: name,
    ref: setInputNode,
    onChange: handleChange
  }));
};
export default UploadForm;